import { LucideIcon, MoreVertical } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { cn } from "@/lib/utils";
import { ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { genieMenuProps } from "@/lib/genie/dom-snapshot";

export type IMenuEntry = {
  // Distinguishes regular menu items ("action", default) from non-clickable
  // visual elements: "separator" renders a horizontal rule, "label" renders a
  // muted section heading. Only `kind: "label"` reads `label`; "separator"
  // ignores all other fields.
  kind?: "action" | "separator" | "label";
  label?: string | ReactNode;
  // extra classes for a `kind: "label"` heading (e.g. override the font weight)
  labelClassName?: string;
  icon?: LucideIcon;
  onClick?: () => void;
  color?: string;
  auth?: {
    level: "tenant" | "group";
    permissions: string[];
    requiresAllPermissions?: boolean;
  };
  isHidden?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

// The items a menu shows, put on its trigger for Genie's page snapshot.
export function genieMenu(items: IMenuEntry[]): Record<string, string> {
  return genieMenuProps(
    items.flatMap((item) =>
      (item.kind ?? "action") === "action" &&
      !item.isHidden &&
      typeof item.label === "string"
        ? [item.disabled ? `${item.label} (disabled)` : item.label]
        : []
    )
  );
}

export const NoxusMenu = ({
  itemsArray,
  align = "end",
  side = "bottom",
  isDisabled,
  isSmall = false,
  ariaLabel = "Open menu",
  children,
}: {
  itemsArray: IMenuEntry[];
  align?: "start" | "center" | "end";
  side?: "top" | "bottom" | "left" | "right";
  isDisabled?: boolean;
  isSmall?: boolean;
  /** Names the icon-only trigger for screen readers. Ignored when `children`
   * supplies its own trigger. */
  ariaLabel?: string;
  children?: ReactNode;
}) => {
  const { hasAnyPermission, hasAllPermissions } = usePermissions();

  const isItemVisible = (item: IMenuEntry): boolean => {
    // Separators and section labels aren't auth-gated.
    if (item.kind === "separator" || item.kind === "label") return true;
    if (item.isHidden) return false;
    if (!item.auth) return true;
    const { level, permissions, requiresAllPermissions = false } = item.auth;
    return requiresAllPermissions
      ? hasAllPermissions(permissions, level)
      : hasAnyPermission(permissions, level);
  };

  const visibleItems = itemsArray.filter(isItemVisible);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        disabled={isDisabled}
        {...genieMenu(visibleItems)}
      >
        {children ? (
          children
        ) : (
          <Button
            variant="ghost"
            size={isSmall ? "iconsm" : "icon"}
            aria-label={ariaLabel}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        onClick={(e) => e.stopPropagation()}
        className="w-auto"
      >
        {visibleItems.map((item, index) => {
          if (item.kind === "separator") {
            return <DropdownMenuSeparator key={`menu-sep-${index}`} />;
          }
          if (item.kind === "label") {
            return (
              <DropdownMenuLabel
                key={`menu-label-${index}`}
                className={cn(
                  "text-xs text-muted-foreground font-normal",
                  item.labelClassName
                )}
              >
                {item.label}
              </DropdownMenuLabel>
            );
          }
          const Icon = item.icon;
          const menuItem = (
            <DropdownMenuItem
              key={`menu-item-${index}`}
              onClick={item.disabled ? undefined : item.onClick}
              className={cn(
                "cursor-pointer",
                item.disabled && "opacity-50 cursor-not-allowed"
              )}
              onSelect={item.disabled ? (e) => e.preventDefault() : undefined}
            >
              {Icon && (
                <Icon
                  className="h-4 w-4"
                  color={
                    item.color === "destructive"
                      ? "var(--destructive)"
                      : "var(--muted-foreground)"
                  }
                />
              )}
              <span
                className={cn("text-sm", item.color && `text-${item.color}`)}
              >
                {item.label}
              </span>
            </DropdownMenuItem>
          );

          if (item.disabled && item.tooltip) {
            return (
              <Tooltip key={`menu-item-${index}`}>
                <TooltipTrigger asChild>{menuItem}</TooltipTrigger>
                <TooltipContent side="left">{item.tooltip}</TooltipContent>
              </Tooltip>
            );
          }

          return menuItem;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
